import { AssessmentLayer, AssessmentTab } from "./types";
import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { useCallback, useEffect, useMemo, useReducer } from "react";

import { Layer } from "../map/Multilayer/types";
import { RenderQueueProvider } from "react-migration/domains/sites/card/Map/RenderQueueContext";
import { SiteOptions } from "./siteOptions";
import clsx from "clsx";
import { tabTranslationKey } from "./utils";
import { useAssessmentTabs } from "./useAssessmentTabs";
import { useCheckLayerAccess } from "../map/Multilayer/Bundle/useLayerAccess";
import { useSelectionHandler } from "../map/Multilayer/selection_types/useSelectionHandler";
import { useSiteById } from "react-migration/domains/sites/hooks/useSiteById";
import { useSiteReportLogo } from "./useSiteReportLogo";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

function isLayerActive(
  layer: Layer,
  activeAssessmentLayers: AssessmentLayer[],
  tabKey: string,
  groupId: string
): boolean {
  return activeAssessmentLayers.some(
    (activeLayer) =>
      activeLayer.layerId === layer.id && activeLayer.tabKey === tabKey && activeLayer.groupId === groupId
  );
}

function filterGroupLayers(
  groupLayers: Layer[],
  activeAssessmentLayers: AssessmentLayer[],
  tabKey: string,
  groupId: string,
  checkLayerAccess: (layer: Layer) => boolean
): Layer[] {
  return groupLayers.filter((layer: Layer) => {
    const isActive = isLayerActive(layer, activeAssessmentLayers, tabKey, groupId);
    const hasAccess = checkLayerAccess(layer);
    return isActive && hasAccess;
  });
}

function processTabGroups(
  tabData: AssessmentTab,
  activeAssessmentLayers: AssessmentLayer[],
  checkLayerAccess: (layer: Layer) => boolean,
  result: Record<string, Layer[]>
): void {
  tabData.groups.forEach((group) => {
    const filteredLayers = filterGroupLayers(
      group.layers,
      activeAssessmentLayers,
      tabData.key,
      group.id,
      checkLayerAccess
    );

    if (filteredLayers.length === 0) {
      return;
    }

    if (group.layers.length > 1) {
      result[group.id] = filteredLayers;
    } else {
      if (!result[tabData.key]) {
        result[tabData.key] = [];
      }
      result[tabData.key].push(...filteredLayers);
    }
  });
}

function useFilteredAssessmentTabs(activeAssessmentLayers: AssessmentLayer[]): Record<string, Layer[]> {
  const assessmentTabs = useAssessmentTabs();
  const checkLayerAccess = useCheckLayerAccess();

  return useMemo(() => {
    const result: Record<string, Layer[]> = {};

    assessmentTabs.forEach((tabData) => {
      processTabGroups(tabData, activeAssessmentLayers, checkLayerAccess, result);
    });

    return Object.fromEntries(Object.entries(result).filter(([, layers]) => layers.length > 0));
  }, [activeAssessmentLayers, assessmentTabs, checkLayerAccess]);
}

interface SiteReportContentProps {
  isShared?: boolean;
  activeAssessmentLayers: AssessmentLayer[];
  siteOptions: string[];
  selection: SelectionFeature;
  onReportLoaded(hasLoaded: boolean, loadingState: SiteReportLoadingState): void;
}

export enum LayerLoadingActionType {
  MOUNT = "mount",
  UNMOUNT = "unmount",
  LOAD = "load",
  TERMINATE = "terminate",
}

export type LayerLoadingState = {
  mounted: boolean;
  loading: boolean;
  error?: string;
};

export type SiteReportLoadingState = Record<string, LayerLoadingState>;

type LayerLoadingAction =
  | { type: LayerLoadingActionType.MOUNT; ids: string[] }
  | { type: LayerLoadingActionType.LOAD; id: string }
  | { type: LayerLoadingActionType.UNMOUNT; id: string }
  | { type: LayerLoadingActionType.TERMINATE; id: string; error: string };

const siteReportLoadingReducer = (
  state: SiteReportLoadingState,
  action: LayerLoadingAction
): SiteReportLoadingState => {
  let newLayerState: LayerLoadingState;
  switch (action.type) {
    case LayerLoadingActionType.LOAD:
      newLayerState = {
        mounted: true,
        loading: false,
      };
      break;
    case LayerLoadingActionType.UNMOUNT:
      newLayerState = {
        mounted: false,
        loading: false,
        error: state[action.id]?.error,
      };
      break;
    case LayerLoadingActionType.TERMINATE:
      newLayerState = {
        mounted: true,
        loading: false,
        error: action.error,
      };
      break;
    case LayerLoadingActionType.MOUNT: {
      const stateUpdate = action.ids.reduce((newState, id) => {
        if (!state[id]?.mounted) {
          newState[id] = {
            mounted: true,
            loading: true,
          };
        }
        return newState;
      }, {} as SiteReportLoadingState);
      return {
        ...state,
        ...stateUpdate,
      };
    }
  }
  return {
    ...state,
    [action.id]: newLayerState,
  };
};

/**
 * Component that renders configured site report content.
 * Used in both the preview & by the print wrapper.
 */
export function SiteReportContent({
  isShared,
  activeAssessmentLayers,
  siteOptions,
  selection,
  onReportLoaded,
}: Readonly<SiteReportContentProps>) {
  const { t } = useTranslation();
  const { logo } = useSiteReportLogo(selection.id!);

  const SelectionHandler = useSelectionHandler(selection);

  const filteredAssessmentTabs = useFilteredAssessmentTabs(activeAssessmentLayers);
  const [summaryLoaded, setSummaryLoaded] = useReducer(() => true, false);

  const [siteReportLoadingState, updateSiteReportLoadingState] = useReducer(
    siteReportLoadingReducer,
    Object.values(filteredAssessmentTabs)
      .flat()
      .reduce((initialState, layer) => {
        initialState[layer.id] = {
          mounted: true,
          loading: true,
        };
        return initialState;
      }, {} as SiteReportLoadingState)
  );

  useEffect(() => {
    const newLayerIds = Object.values(filteredAssessmentTabs)
      .flat()
      .map((layer) => layer.id)
      .filter((layerId) => !siteReportLoadingState[layerId]?.mounted);
    if (newLayerIds.length) {
      updateSiteReportLoadingState({ type: LayerLoadingActionType.MOUNT, ids: newLayerIds });
    }
  }, [filteredAssessmentTabs, siteReportLoadingState]);

  useEffect(() => {
    const hasSummaryLoaded = !SelectionHandler.SelectionPrintableSummary || summaryLoaded;

    const hasAllLayersLoaded = Object.values(siteReportLoadingState).every((layerState) => {
      return !layerState.mounted || (!layerState.loading && !layerState.error);
    });

    onReportLoaded(hasAllLayersLoaded && hasSummaryLoaded, siteReportLoadingState);
  }, [SelectionHandler.SelectionPrintableSummary, onReportLoaded, siteReportLoadingState, summaryLoaded]);

  return (
    <RenderQueueProvider>
      <div className="[&>div]:mb-4 [&>div:last-child]:mb-0">
        {logo && (
          <div data-testid="report-logo" className="flex justify-center w-full mt-4 mb-4">
            <img id="output" alt="logo" placeholder="Upload Logo" className="h-40 mb-4" src={logo.data} />
          </div>
        )}
        {SelectionHandler.SelectionPrintableSummary && (
          <SelectionHandler.SelectionPrintableSummary selection={selection} onLoaded={setSummaryLoaded} />
        )}

        {isSiteSelection(selection) && siteOptions.includes(SiteOptions.SITE_DESCRIPTION) && (
          <SiteDescription selection={selection} isShared={isShared} />
        )}

        {Object.entries(filteredAssessmentTabs)
          .filter(([, layers]) => !!layers.length)
          .map(([tab, layers]) => (
            <div
              className="hidden has-[div]:block [&>div]:mb-4 [&>div:last-child]:mb-0 print:break-after-page print:break-inside-avoid"
              key={tab}
            >
              <h2 className="mb-2">{t(tabTranslationKey(tab))}</h2>

              {layers.map((layer, index) => (
                <SiteReportLayer
                  key={layer.id}
                  layer={layer}
                  selection={selection}
                  updateSiteReportLoadingState={updateSiteReportLoadingState}
                  first={index === 0}
                />
              ))}
            </div>
          ))}
      </div>
    </RenderQueueProvider>
  );
}

function isSiteSelection(selection: SelectionFeature) {
  return selection.type === SelectionType.SITE || selection.type === SelectionType.SHARED_SITE;
}

interface SiteDescriptionProps {
  selection: SelectionFeature;
  isShared?: boolean;
}

function SiteDescription({ selection, isShared = false }: Readonly<SiteDescriptionProps>) {
  const { site } = useSiteById(selection.id, { isShared });

  if (!site?.notes) return null;

  return (
    <div>
      <h3>Site Description</h3>
      <p>{site?.notes ? site.notes : "No Description"}</p>
    </div>
  );
}

type SiteReportLayerProps = Readonly<{
  layer: Layer;
  selection: SelectionFeature;
  updateSiteReportLoadingState: React.Dispatch<LayerLoadingAction>;
  first?: boolean;
}>;

function SiteReportLayer({ layer, selection, updateSiteReportLoadingState, first }: SiteReportLayerProps) {
  const Printable = layer.layerType.Printable;

  const onLoaded = useCallback(
    (error?: string) => {
      if (error) {
        updateSiteReportLoadingState({
          type: LayerLoadingActionType.TERMINATE,
          id: layer.id,
          error,
        });
      } else {
        updateSiteReportLoadingState({ type: LayerLoadingActionType.LOAD, id: layer.id });
      }
    },
    [layer.id, updateSiteReportLoadingState]
  );

  const onUnmount = useCallback(() => {
    updateSiteReportLoadingState({ type: LayerLoadingActionType.UNMOUNT, id: layer.id });
  }, [layer.id, updateSiteReportLoadingState]);

  if (!Printable) return null;

  return (
    <section
      className={clsx("empty:hidden print:break-inside-avoid", {
        "print:break-before-avoid": first,
      })}
    >
      <Printable selection={selection} layer={layer} onLoaded={onLoaded} onUnmount={onUnmount} />
    </section>
  );
}
